import { makeStyles } from '@material-ui/styles'
import DropdownField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import TextField from 'pf9-ui-components/built/components/validatedForm/TextField'
import AsyncDropdown from 'pf9-ui-components/built/elements/dropdown/AsyncDropdown'
import type Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { type ReactNode, useMemo } from 'react'
import SimpleLink from 'core/components/SimpleLink'
import Text from 'core/elements/Text'
import { customValidator } from 'core/utils/fieldValidators'
import {
  ETCD_BACKUP_DEFAULT_MAX_COUNT,
  ETCD_BACKUP_DEFAULT_STORAGE_PATH,
  ETCD_BACKUP_DEFAULT_TIME_ZONE,
  ETCD_BACKUP_MAX_COUNT,
  ETCD_BACKUP_MIN_COUNT,
  ETCD_BACKUP_SCHEDULE_LABELS,
  ETCD_BACKUP_SCHEDULE_NAMES,
  type EtcdBackupScheduleName,
  etcdBackupWeekdayOptions,
  getStoragePathError,
  getTimeZoneOptions,
  isValidCronSchedule,
  isValidDailyTime,
  isValidMaxCount,
  isValidMinute,
} from 'k8s/components/kaapi/cluster-addons/etcd-backup'

interface EtcdBackupFieldsProps {
  params: Record<string, unknown>
  getParamsUpdater: (key: string) => (value: unknown) => void
}

const storagePathValidator = customValidator(
  (value: string) => !getStoragePathError(value),
  (value: string) => getStoragePathError(value),
)
const timeValidator = customValidator(
  (value: string) => isValidDailyTime(value),
  'Enter the time as HH:MM, from 00:00 to 23:59',
)
const minuteValidator = customValidator(
  (value: string) => isValidMinute(value),
  'Enter a minute from 0 to 59',
)
const scheduleValidator = customValidator(
  (value: string) => isValidCronSchedule(value),
  'Enter a 5-field cron schedule: minute, hour, day of month, month and day of week',
)
const maxCountValidator = customValidator(
  (value: string) => isValidMaxCount(value),
  `Enter a whole number from ${ETCD_BACKUP_MIN_COUNT} to ${ETCD_BACKUP_MAX_COUNT}`,
)

const maxCountInfo = `How many of this schedule's backups each control plane node keeps, from ${ETCD_BACKUP_MIN_COUNT} to ${ETCD_BACKUP_MAX_COUNT}. Leave empty for ${ETCD_BACKUP_DEFAULT_MAX_COUNT}.`

// The pf9 TextField's typings require these pointer props
const textFieldProps = {
  crossOrigin: undefined,
  onPointerEnterCapture: undefined,
  onPointerLeaveCapture: undefined,
}

export default function EtcdBackupFields({ params, getParamsUpdater }: EtcdBackupFieldsProps) {
  const classes = useStyles()
  const timeZoneOptions = useMemo(() => getTimeZoneOptions(), [])
  const added = (params.etcdBackupSchedules as EtcdBackupScheduleName[] | undefined) ?? []
  const setSchedules = getParamsUpdater('etcdBackupSchedules')
  // EBF-FR-007: only the schedule types not added yet
  const addOptions = ETCD_BACKUP_SCHEDULE_NAMES.filter((name) => !added.includes(name)).map(
    (name) => ({ label: ETCD_BACKUP_SCHEDULE_LABELS[name], value: name }),
  )

  const maxCountField = (id: string) => (
    <TextField
      id={id}
      {...textFieldProps}
      label="Max Backups per Node"
      placeholder={String(ETCD_BACKUP_DEFAULT_MAX_COUNT)}
      value={params[id] as string}
      onChange={getParamsUpdater(id)}
      validations={[maxCountValidator]}
      info={maxCountInfo}
    />
  )

  // EBF-FR-003: each schedule's own fields
  const scheduleFields: Record<EtcdBackupScheduleName, ReactNode> = {
    hourly: (
      <div className={classes.row}>
        <TextField
          id="etcdBackupHourlyMinute"
          {...textFieldProps}
          label="Minute of the Hour"
          placeholder="0-59"
          value={params.etcdBackupHourlyMinute as string}
          onChange={getParamsUpdater('etcdBackupHourlyMinute')}
          validations={[minuteValidator]}
          info="The backup runs every hour at this minute."
          required
        />
        {maxCountField('etcdBackupHourlyMaxCount')}
      </div>
    ),
    daily: (
      <div className={classes.row}>
        <TextField
          id="etcdBackupDailyTime"
          {...textFieldProps}
          label="Backup Time"
          placeholder="HH:MM"
          value={params.etcdBackupDailyTime as string}
          onChange={getParamsUpdater('etcdBackupDailyTime')}
          validations={[timeValidator]}
          required
        />
        {maxCountField('etcdBackupDailyMaxCount')}
      </div>
    ),
    weekly: (
      <>
        <div className={classes.row}>
          <DropdownField
            id="etcdBackupWeeklyDay"
            label="Day of the Week"
            DropdownComponent={AsyncDropdown}
            items={etcdBackupWeekdayOptions}
            placeholder="Select a day"
            value={params.etcdBackupWeeklyDay as string}
            onChange={getParamsUpdater('etcdBackupWeeklyDay')}
            required
          />
          <TextField
            id="etcdBackupWeeklyTime"
            {...textFieldProps}
            label="Backup Time"
            placeholder="HH:MM"
            value={params.etcdBackupWeeklyTime as string}
            onChange={getParamsUpdater('etcdBackupWeeklyTime')}
            validations={[timeValidator]}
            required
          />
        </div>
        <div className={classes.row}>{maxCountField('etcdBackupWeeklyMaxCount')}</div>
      </>
    ),
    custom: (
      <div className={classes.row}>
        <TextField
          id="etcdBackupCustomSchedule"
          {...textFieldProps}
          label="Cron Schedule"
          placeholder="0 1,9 * * *"
          value={params.etcdBackupCustomSchedule as string}
          onChange={getParamsUpdater('etcdBackupCustomSchedule')}
          validations={[scheduleValidator]}
          info="A 5-field cron schedule in the selected time zone, such as 0 1,9 * * * for 01:00 and 09:00. Runs must be at least 5 minutes apart."
          required
        />
        {maxCountField('etcdBackupCustomMaxCount')}
      </div>
    ),
  }

  return (
    <div className={classes.etcdBackupFields} data-testid="etcd-backup-fields">
      <div className={classes.header}>
        <Text variant="caption1">Backup Settings</Text>
        {/* EBF-FR-005: where the backups are kept */}
        <Text variant="body2" className={classes.hint} data-testid="etcd-backup-hint">
          Each backup run snapshots the etcd member of one control plane node and keeps the backup
          on that node. Each schedule keeps its own backups, and the max backups count is per
          control plane node. Backups are lost when their node is replaced, as in a Kubernetes
          upgrade or node repair, so copy the ones you need elsewhere.
        </Text>
      </div>
      <div className={classes.row}>
        {/* EBF-FR-001: storage path, empty means the default */}
        <TextField
          id="etcdBackupStoragePath"
          {...textFieldProps}
          label="Storage Path"
          placeholder={ETCD_BACKUP_DEFAULT_STORAGE_PATH}
          value={params.etcdBackupStoragePath as string}
          onChange={getParamsUpdater('etcdBackupStoragePath')}
          validations={[storagePathValidator]}
          info="The directory on each control plane node that holds its backups. Leave empty for the default."
        />
        {/* EBF-FR-002: every schedule runs in this time zone; empty means UTC */}
        <DropdownField
          id="etcdBackupTimeZone"
          label="Time Zone"
          DropdownComponent={AsyncDropdown}
          items={timeZoneOptions}
          placeholder={ETCD_BACKUP_DEFAULT_TIME_ZONE}
          value={params.etcdBackupTimeZone as string}
          onChange={getParamsUpdater('etcdBackupTimeZone')}
          enableSearch
        />
      </div>

      {/* EBF-FR-007: add a schedule, in the right column under Time Zone so it is easy to
          find; at least one schedule is needed */}
      {addOptions.length > 0 && (
        <div className={classes.row} data-testid="etcd-backup-add-schedule">
          <div />
          {/* A new key after each add or remove re-creates the dropdown, so it shows
              "Select a schedule" again instead of the last pick */}
          <AsyncDropdown
            key={added.join(',')}
            label="Add Schedule"
            items={addOptions}
            value=""
            placeholder="Select a schedule"
            onChange={(name) => {
              if (name) setSchedules([...added, name as EtcdBackupScheduleName])
            }}
          />
        </div>
      )}

      {ETCD_BACKUP_SCHEDULE_NAMES.filter((name) => added.includes(name)).map((name) => (
        <div key={name} className={classes.schedule} data-testid={`etcd-backup-schedule-${name}`}>
          <div className={classes.scheduleHeader}>
            <Text variant="caption1">{`${ETCD_BACKUP_SCHEDULE_LABELS[name]} Backup`}</Text>
            {/* EBF-FR-004: remove a schedule */}
            <SimpleLink
              onClick={() => setSchedules(added.filter((n) => n !== name))}
              data-testid={`etcd-backup-remove-${name}`}
            >
              Remove
            </SimpleLink>
          </div>
          {scheduleFields[name]}
        </div>
      ))}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  etcdBackupFields: {
    display: 'grid',
    gridGap: theme.spacing(2),
    paddingTop: theme.spacing(2),
    // EBF-FR-005: room below the last schedule, before the end of the form
    paddingBottom: theme.spacing(3),
    borderTop: `1px solid ${theme.components.card.border}`,
  },
  header: {
    display: 'grid',
    gridGap: theme.spacing(1),
  },
  // Two equal columns, like the Kubernetes Version and Available Images row
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gridGap: theme.spacing(2),
    alignItems: 'start',
  },
  schedule: {
    display: 'grid',
    gridGap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.components.card.border}`,
    borderRadius: 4,
  },
  scheduleHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  hint: {
    color: theme.components.typography.passive,
  },
}))
